/**
 * EoT Claim Builder — Entitlement.
 *
 * The measured days become granted days under the claim's concurrency rule, and
 * money under whichever prolongation basis the claim adopts. Everything here is
 * computed by the backend; the only things typed in are the cost inputs.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Save, Calculator, Database, Plus, Trash2, AlertTriangle } from 'lucide-react';

const FIELDS = [
  { key: 'contractSum', label: 'Contract sum', hint: 'the current contract value' },
  { key: 'contractPeriodDays', label: 'Contract period (days)', hint: 'start to original completion' },
  { key: 'hudsonPercent', label: 'Tendered overhead + profit %', hint: 'Hudson' },
  { key: 'emdenPercent', label: 'Audited overhead + profit %', hint: 'Emden' },
  { key: 'contractBillings', label: 'Billings on this contract', hint: 'Eichleay' },
  { key: 'totalBillings', label: 'Total billings in the period', hint: 'Eichleay' },
  { key: 'totalHeadOfficeOverhead', label: 'Total head-office overhead', hint: 'Eichleay' },
  { key: 'actualPerformanceDays', label: 'Days of actual performance', hint: 'Eichleay' },
  { key: 'siteOverheadPerDay', label: 'Site overhead per day', hint: 'actual build-up' },
  { key: 'actualRecordedCost', label: 'Recorded time-related cost', hint: 'actual build-up' },
  { key: 'financeChargePercent', label: 'Finance charge %', hint: 'applied to the subtotal' },
  { key: 'mitigationCredit', label: 'Mitigation credit', hint: 'deducted from the total' },
];

const n = (v) => (v == null || v === '' ? '—'
  : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const days = (v) => (v == null || v === '' ? '—'
  : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }));
const methodLabel = (m) => String(m || '').replaceAll('_', ' ').replace(/\btia\b/i, 'TIA')
  .replace(/^./, c => c.toUpperCase());

export default function EntitlementTab({ claimId, claim, onSaved }) {
  const [data, setData] = useState(null);
  const [inputs, setInputs] = useState({});
  const [heads, setHeads] = useState([]);
  const [adopted, setAdopted] = useState('ACTUAL');
  const [source, setSource] = useState('MANUAL');
  const [currency, setCurrency] = useState('');
  const [runMethod, setRunMethod] = useState('');
  const [records, setRecords] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(async (method = '') => {
    const { data: d } = await api.get(`/eot/claims/${claimId}/entitlement${method ? `?method=${method}` : ''}`);
    setData(d);
    setCurrency(d.currency || '');
    if (d.entitlement) {
      setInputs(d.entitlement.inputs || {});
      setHeads((d.entitlement.heads || []).slice(1).map(h => ({ head: h.head, amount: h.amount, note: h.note })));
      setAdopted(d.entitlement.adoptedMethod);
      setSource(d.entitlement.inputSource || 'MANUAL');
      setRunMethod(d.entitlement.days?.method || '');
    }
  }, [claimId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async () => {
    setBusy('save');
    try {
      const { data: d } = await api.put(`/eot/claims/${claimId}/entitlement`, {
        runMethod, adoptedMethod: adopted, inputSource: source, currency, inputs, additionalHeads: heads,
      });
      setData(prev => ({ ...prev, entitlement: d.entitlement, savedAt: d.savedAt, savedBy: d.savedBy }));
      toast.success('Entitlement position saved');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const pullCosts = async () => {
    setBusy('costs');
    try {
      const { data: d } = await api.get(`/eot/claims/${claimId}/cost-records`);
      setRecords(d);
      setPicked([]);
      setSource('PROJECT_COSTS');
      toast.success(`${d.transactionCount} cost records read — tick the time-related ones`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read the cost records'); }
    finally { setBusy(''); }
  };

  const togglePick = (code) => {
    const next = picked.includes(code) ? picked.filter(c => c !== code) : [...picked, code];
    setPicked(next);
    const total = (records?.groups || []).filter(g => next.includes(g.code))
      .reduce((sum, g) => sum + Number(g.amount || 0), 0);
    setInputs(prev => ({ ...prev, actualRecordedCost: Math.round(total * 100) / 100 }));
  };

  if (!data) return <div className="pt-6 text-xs text-muted-foreground" data-testid="eot-entitlement-loading">Loading…</div>;

  if (!data.entitlement) {
    return (
      <p className="pt-6 text-xs text-muted-foreground" data-testid="eot-entitlement-blocked">{data.message}</p>
    );
  }

  const e = data.entitlement;
  const d = e.days;

  return (
    <div className="space-y-4 pt-4" data-testid="eot-entitlement-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-foreground font-medium">Entitlement</p>
          <p className="text-[11px] text-muted-foreground">
            Computed from the {methodLabel(d.method)} run under the{' '}
            {String(e.days.concurrencyRule || '').replaceAll('_', ' ').toLowerCase()} rule.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={runMethod} onChange={ev => { setRunMethod(ev.target.value); load(ev.target.value); }}
                  data-testid="eot-entitlement-run"
                  className="h-9 rounded-md border border-border bg-background text-foreground text-xs px-2">
            {(data.runs || []).map(r => <option key={r.method} value={r.method}>
              {methodLabel(r.method)}</option>)}
          </select>
          <Input value={currency} onChange={ev => setCurrency(ev.target.value)} placeholder="currency"
                 className="h-9 w-24 text-xs" data-testid="eot-entitlement-currency" />
          <Button size="sm" variant="outline" onClick={pullCosts} disabled={busy === 'costs'}
                  data-testid="eot-pull-costs">
            {busy === 'costs' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Database className="w-3.5 h-3.5 mr-1.5" />}
            Pull project costs
          </Button>
          <Button size="sm" onClick={save} disabled={busy === 'save'} data-testid="eot-save-entitlement"
                  className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]">
            {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save position
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['Net extension sought', `${days(d.netExtensionDays)} d`, 'text-foreground', 'eot-ent-days'],
          ['Compensable period', `${days(d.compensableDays)} d`, 'text-emerald-600', 'eot-ent-compensable'],
          ['Concurrent delay', `${days(d.concurrentDays)} d`, 'text-amber-500', 'eot-ent-concurrent'],
          ['Total claimed', `${currency} ${n(e.totalClaimed)}`, 'text-[#6B5CE7]', 'eot-ent-total']]
          .map(([label, value, tone, tid]) => (
            <div key={label} className="rounded-lg border border-border p-3" data-testid={tid}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className={`text-lg font-semibold ${tone}`}>{value}</p>
            </div>
          ))}
      </div>

      <div className="rounded-lg border border-border p-3">
        <p className="text-xs text-foreground font-medium mb-2">How the days were arrived at</p>
        {(d.basis || []).map((line, i) => (
          <p key={i} className="text-[11px] text-muted-foreground mb-1" data-testid={`eot-ent-basis-${i}`}>▪ {line}</p>
        ))}
      </div>

      <div className="rounded-lg border border-border p-3 space-y-3">
        <p className="text-xs text-foreground font-medium">Cost inputs</p>
        <div className="grid md:grid-cols-4 gap-2">
          {FIELDS.map(f => (
            <div key={f.key}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" title={f.hint}>{f.label}</p>
              <Input type="number" value={inputs[f.key] ?? ''} data-testid={`eot-input-${f.key}`}
                     onChange={ev => setInputs(p => ({ ...p, [f.key]: ev.target.value }))}
                     className="h-8 text-xs" />
            </div>
          ))}
        </div>
        {records && (
          <div className="rounded-md border border-border p-2 space-y-1" data-testid="eot-cost-records">
            <p className="text-[11px] text-foreground">
              {records.transactionCount} cost records between {records.from} and {records.to}
              {records.compensableDays ? ` — ${days(records.compensableDays)} compensable day(s)` : ''}. {records.note}
            </p>
            {(records.groups || []).slice(0, 12).map(g => (
              <label key={g.code} className="flex items-center gap-2 text-[10px] text-muted-foreground cursor-pointer"
                     data-testid={`eot-cost-group-${g.code}`}>
                <input type="checkbox" checked={picked.includes(g.code)} onChange={() => togglePick(g.code)} />
                <span className="text-foreground">{g.code}</span>
                <span>{currency} {n(g.amount)} across {g.count} record(s)
                  {g.perDay ? ` · ${currency} ${n(g.perDay)}/day` : ''}</span>
                <span className="truncate max-w-[240px]">{g.sample}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border p-3 space-y-2">
        <p className="text-xs text-foreground font-medium">Prolongation cost — all four bases</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="eot-bases-table">
            <thead className="text-muted-foreground uppercase text-[10px]">
              <tr>{['Adopt', 'Basis', 'Daily rate', 'Amount', 'Workings', 'Missing'].map(h =>
                <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
            </thead>
            <tbody>
              {Object.entries(e.bases).map(([key, b]) => (
                <tr key={key} className={`border-t border-border align-top ${adopted === key ? 'bg-[#6B5CE7]/10' : ''}`}
                    data-testid={`eot-basis-${key}`}>
                  <td className="px-2 py-1.5">
                    <input type="radio" name="basis" checked={adopted === key} onChange={() => setAdopted(key)}
                           data-testid={`eot-adopt-${key}`} />
                  </td>
                  <td className="px-2 py-1.5 text-foreground">{b.label}</td>
                  <td className="px-2 py-1.5">{currency} {n(b.dailyRate)}</td>
                  <td className="px-2 py-1.5 font-semibold text-foreground">{currency} {n(b.amount)}</td>
                  <td className="px-2 py-1.5 text-[10px] text-muted-foreground max-w-[320px]">
                    {b.formula}<br /><span className="text-foreground/70">{b.workings}</span>
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-amber-600">{(b.missing || []).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-foreground font-medium">Sum claimed</p>
          <Button size="sm" variant="outline" data-testid="eot-add-head"
                  onClick={() => setHeads(p => [...p, { head: '', amount: '', note: '' }])}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />Add a head of claim
          </Button>
        </div>
        {heads.map((h, i) => (
          <div key={i} className="grid md:grid-cols-[2fr_1fr_2fr_auto] gap-2" data-testid={`eot-head-${i}`}>
            <Input value={h.head} placeholder="Head of claim (e.g. disruption, acceleration)"
                   onChange={ev => setHeads(p => p.map((x, j) => j === i ? { ...x, head: ev.target.value } : x))}
                   className="h-8 text-xs" data-testid={`eot-head-name-${i}`} />
            <Input type="number" value={h.amount} placeholder="amount"
                   onChange={ev => setHeads(p => p.map((x, j) => j === i ? { ...x, amount: ev.target.value } : x))}
                   className="h-8 text-xs" data-testid={`eot-head-amount-${i}`} />
            <Input value={h.note} placeholder="note"
                   onChange={ev => setHeads(p => p.map((x, j) => j === i ? { ...x, note: ev.target.value } : x))}
                   className="h-8 text-xs" />
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`eot-head-delete-${i}`}
                    onClick={() => setHeads(p => p.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
        <table className="w-full text-xs" data-testid="eot-total-table">
          <tbody>
            {(e.heads || []).map((h, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-2 py-1.5 text-foreground">{h.head}</td>
                <td className="px-2 py-1.5 text-right">{currency} {n(h.amount)}</td>
              </tr>
            ))}
            <tr className="border-t border-border">
              <td className="px-2 py-1.5 text-muted-foreground">Subtotal</td>
              <td className="px-2 py-1.5 text-right">{currency} {n(e.subtotal)}</td>
            </tr>
            {!!e.financeCharge && (
              <tr className="border-t border-border">
                <td className="px-2 py-1.5 text-muted-foreground">Finance charge at {n(e.financeChargePercent)}%</td>
                <td className="px-2 py-1.5 text-right">{currency} {n(e.financeCharge)}</td>
              </tr>
            )}
            {!!e.mitigationCredit && (
              <tr className="border-t border-border">
                <td className="px-2 py-1.5 text-muted-foreground">Less mitigation credit</td>
                <td className="px-2 py-1.5 text-right">-{currency} {n(e.mitigationCredit)}</td>
              </tr>
            )}
            <tr className="border-t border-border">
              <td className="px-2 py-1.5 text-foreground font-semibold">Total claimed</td>
              <td className="px-2 py-1.5 text-right font-semibold text-[#6B5CE7]" data-testid="eot-total-claimed">
                {currency} {n(e.totalClaimed)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {(e.gaps || []).length > 0 && (
        <p className="text-[11px] text-amber-600 flex items-start gap-1.5" data-testid="eot-entitlement-gaps">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          The adopted basis is missing {e.gaps.join(', ')} — fill those in before the pack goes out.
        </p>
      )}
      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <Badge variant="outline" className="text-[9px]">deterministic</Badge>
        <Calculator className="w-3 h-3" /> Every rate and total above is arithmetic on your inputs and the measured days.
        {data.savedAt ? ` Last saved ${String(data.savedAt).slice(0, 16).replace('T', ' ')} by ${data.savedBy}.` : ''}
      </p>
    </div>
  );
}
